import React, { useState, useEffect, useCallback } from 'react';
import { Package, Search, X, ChevronDown, Check, Truck, Clock, XCircle, ShoppingBag, AlertTriangle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { authFetch, API } from '../../utils/api';


const STATUS_CONFIG = {
  pending:    { label: 'Pending',    color: 'text-yellow-400', bg: 'bg-yellow-500/15', icon: Clock },
  processing: { label: 'Processing', color: 'text-blue-400',   bg: 'bg-blue-500/15',   icon: Package },
  shipped:    { label: 'Shipped',    color: 'text-cyan-400',   bg: 'bg-cyan-500/15',   icon: Truck },
  delivered:  { label: 'Delivered',  color: 'text-green-400',  bg: 'bg-green-500/15',  icon: Check },
  cancelled:  { label: 'Cancelled',  color: 'text-red-400/60', bg: 'bg-red-500/10',    icon: XCircle },
};

const RARITY_COLORS = {
  common: 'text-slate-400',
  uncommon: 'text-green-400',
  rare: 'text-blue-400',
  epic: 'text-violet-400',
  legendary: 'text-amber-400',
  mythic: 'text-rose-400',
};

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [counts, setCounts] = useState({});
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [updatingId, setUpdatingId] = useState(null);

  const fetchOrders = useCallback(async (s = '', st = '') => {
    try {
      const params = new URLSearchParams();
      if (s) params.set('search', s);
      if (st) params.set('status', st);
      const res = await authFetch(`${API}/api/admin/orders?${params}`);
      if (res.ok) {
        const data = await res.json();
        setOrders(data.orders || []);
        setCounts(data.counts || {});
        setTotal(data.total || 0);
      }
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  const handleSearch = (val) => {
    setSearch(val);
    fetchOrders(val, statusFilter);
  };

  const handleStatusFilter = (st) => {
    const next = statusFilter === st ? '' : st;
    setStatusFilter(next);
    fetchOrders(search, next);
  };

  const updateStatus = async (orderId, newStatus) => {
    setUpdatingId(orderId);
    try {
      await authFetch(`${API}/api/admin/orders/${orderId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ status: newStatus }),
      });
      fetchOrders(search, statusFilter);
    } catch {}
    setUpdatingId(null);
  };

  const statusFlow = ['pending', 'processing', 'shipped', 'delivered'];
  const getNextStatus = (current) => {
    const idx = statusFlow.indexOf(current);
    return idx >= 0 && idx < statusFlow.length - 1 ? statusFlow[idx + 1] : null;
  };

  return (
    <div className="space-y-3" data-testid="admin-orders">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Package className="w-3.5 h-3.5 text-primary/60" />
          <span className="text-[10px] uppercase tracking-widest text-muted-foreground/50 font-medium">Treasure Orders</span>
        </div>
        <span className="text-[10px] text-muted-foreground/30">{total} total</span>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground/30" />
        <input
          data-testid="orders-search"
          type="text"
          placeholder="Search by name, email, reward..."
          value={search}
          onChange={e => handleSearch(e.target.value)}
          className="w-full bg-white/[0.03] border border-white/5 focus:border-primary/30 rounded-xl pl-9 pr-3 py-2.5 text-xs outline-none transition-colors placeholder:text-muted-foreground/25"
        />
        {search && (
          <button onClick={() => handleSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2">
            <X className="w-3 h-3 text-muted-foreground/30 hover:text-foreground" />
          </button>
        )}
      </div>

      {/* Status filter pills */}
      <div className="flex flex-wrap gap-1.5">
        {['pending', 'processing', 'shipped', 'delivered', 'cancelled'].map(st => {
          const cfg = STATUS_CONFIG[st];
          const count = counts[st] || 0;
          const isActive = statusFilter === st;
          return (
            <button
              key={st}
              onClick={() => handleStatusFilter(st)}
              data-testid={`orders-filter-${st}`}
              className={`text-[9px] px-2.5 py-1.5 rounded-full inline-flex items-center gap-1 transition-all font-medium ${
                isActive ? `${cfg.bg} ${cfg.color} border border-current/20` : 'bg-white/[0.03] text-muted-foreground/50 border border-white/[0.03] hover:border-white/10'
              }`}
            >
              <cfg.icon className="w-2.5 h-2.5" />
              {cfg.label}
              {count > 0 && <span className="text-[7px] opacity-60">({count})</span>}
            </button>
          );
        })}
      </div>

      {/* Loading */}
      {loading && (
        <div className="text-center py-8">
          <div className="flex justify-center space-x-1.5">
            <div className="w-1.5 h-1.5 bg-primary rounded-full typing-dot" />
            <div className="w-1.5 h-1.5 bg-primary rounded-full typing-dot" />
            <div className="w-1.5 h-1.5 bg-primary rounded-full typing-dot" />
          </div>
        </div>
      )}

      {/* Orders list */}
      {!loading && (
        <div className="space-y-2" data-testid="orders-list">
          {orders.length === 0 && (
            <div className="text-center py-6">
              <ShoppingBag className="w-5 h-5 text-muted-foreground/20 mx-auto mb-1.5" />
              <p className="text-[10px] text-muted-foreground/30">{search || statusFilter ? 'No orders match' : 'No orders yet'}</p>
            </div>
          )}

          {orders.map(order => {
            const cfg = STATUS_CONFIG[order.status] || STATUS_CONFIG.pending;
            const StatusIcon = cfg.icon;
            const rarityColor = RARITY_COLORS[order.milestone_rarity] || 'text-muted-foreground';
            const nextStatus = getNextStatus(order.status);

            return (
              <motion.div
                key={order.order_id}
                initial={{ opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white/[0.02] border border-white/5 rounded-xl p-3.5 space-y-2"
                data-testid={`order-${order.order_id}`}
              >
                {/* Order header */}
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className={`text-xs font-bold ${rarityColor}`}>{order.milestone_name}</span>
                      <span className="text-[8px] bg-white/5 text-muted-foreground/40 px-1.5 py-0.5 rounded-full uppercase">{order.milestone_type}</span>
                    </div>
                    <p className="text-[10px] text-muted-foreground/60">{order.user_name} &middot; {order.user_email}</p>
                  </div>
                  <div className={`inline-flex items-center gap-1 text-[9px] font-medium px-2 py-1 rounded-lg ${cfg.bg} ${cfg.color}`}>
                    <StatusIcon className="w-2.5 h-2.5" />
                    {cfg.label}
                  </div>
                </div>

                {/* Shipping info */}
                {order.shipping_address && (
                  <div className="bg-white/[0.02] border border-white/[0.04] rounded-lg p-2 text-[9px] text-muted-foreground/50">
                    <p className="font-medium text-muted-foreground/70">{order.shipping_name}</p>
                    <p>{order.shipping_address}</p>
                    {order.shipping_note && <p className="italic mt-0.5">{order.shipping_note}</p>}
                  </div>
                )}

                {/* Meta + actions */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-[8px] text-muted-foreground/25">
                    <span>#{order.order_id}</span>
                    <span>{order.created_at ? new Date(order.created_at).toLocaleDateString() : ''}</span>
                    {order.updated_by && <span>by {order.updated_by}</span>}
                  </div>
                  <div className="flex items-center gap-1">
                    {nextStatus && order.status !== 'cancelled' && (
                      <button
                        onClick={() => updateStatus(order.order_id, nextStatus)}
                        disabled={updatingId === order.order_id}
                        className={`text-[9px] font-medium px-2.5 py-1 rounded-lg border transition-all disabled:opacity-40 ${STATUS_CONFIG[nextStatus].bg} ${STATUS_CONFIG[nextStatus].color} border-current/20`}
                        data-testid={`order-advance-${order.order_id}`}
                      >
                        {updatingId === order.order_id ? '...' : `Mark ${STATUS_CONFIG[nextStatus].label}`}
                      </button>
                    )}
                    {order.status !== 'cancelled' && order.status !== 'delivered' && (
                      <button
                        onClick={() => updateStatus(order.order_id, 'cancelled')}
                        disabled={updatingId === order.order_id}
                        className="text-[9px] text-red-400/50 hover:text-red-400 px-2 py-1 rounded-lg transition-colors disabled:opacity-40"
                        data-testid={`order-cancel-${order.order_id}`}
                      >
                        Cancel
                      </button>
                    )}
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}
    </div>
  );
}
